import React, { useMemo, useState } from "react";

/* =========================================================================
   Legendesk Sales — Performance dashboard (original design)
   Two screens: Agents (original) + Teams (rolled-up pods).
   Shared data engine so totals tie out across both views.
   ========================================================================= */

// ---- palette -------------------------------------------------------------
const C = {
  linen: "#F7ECDF", linenDeep: "#EFE1CF", ink: "#0A0C12", ink70: "#3A3D47",
  ink50: "#6B6E78", ink30: "#9A9CA4", card: "#FFFFFF", line: "#E7DAC6",
  lineSoft: "#F0E6D6", indigo: "#4F6EF7", indigoSoft: "#EAEEFF",
  green: "#1F9D6B", greenSoft: "#E4F5EC", amber: "#C9871A", amberSoft: "#FBEFD6",
  red: "#D0503C", redSoft: "#FBE6E0",
};
const FONT_DISP = "'Sora', system-ui, sans-serif";
const FONT_BODY = "'DM Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

// ---- rng -----------------------------------------------------------------
function mulberry32(a) { return function () { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
function hash(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function gauss(r) { return (r() + r() + r()) / 3; }

const FIRST = ["Ayesha", "Bilal", "Zara", "Hamza", "Sana", "Usman", "Mariam", "Faisal", "Noor", "Ahmed", "Hira", "Talha", "Iqra", "Danish", "Fatima", "Saad", "Amna", "Rehan", "Kiran", "Owais", "Mehak", "Zain", "Rida", "Adeel", "Anaya", "Kashif", "Laiba", "Waleed", "Sadia", "Umar", "Aiman", "Junaid", "Sania", "Haris", "Areeba", "Fahad", "Nimra", "Shahzad", "Warda", "Bilawal", "Emma", "Liam", "Sophia", "Noah", "Olivia", "Ethan", "Mia", "Lucas", "Ella", "Mason"];
const LAST = ["Khan", "Malik", "Sheikh", "Raza", "Qureshi", "Butt", "Ansari", "Hashmi", "Siddiqui", "Farooq", "Chaudhry", "Baig", "Awan", "Mirza", "Rana", "Shah", "Gill", "Dar", "Jamil", "Nawaz", "Rivera", "Chen", "Patel", "Nguyen", "Brooks", "Silva"];
const TEAMS = ["Apex", "Titan", "Falcon", "Orbit", "Vanguard"];

function buildAgents(count = 100) {
  const rng = mulberry32(20260703); const out = []; const used = new Set();
  for (let i = 0; i < count; i++) {
    let name; do { name = FIRST[Math.floor(rng() * FIRST.length)] + " " + LAST[Math.floor(rng() * LAST.length)]; } while (used.has(name)); used.add(name);
    const skill = 0.55 + gauss(rng) * 0.85; const vol = 0.05 + rng() * 0.22;
    const baseTarget = 9000 + Math.round(rng() * 17000);
    const refundBase = rng() < 0.16 ? 0.14 + rng() * 0.16 : 0.02 + rng() * 0.09;
    const history = []; let drift = (rng() - 0.5) * 0.06;
    for (let p = 0; p < 6; p++)history.push(Math.max(0.15, skill + drift * (p - 2.5) + (gauss(rng) - 0.5) * vol * 2));
    out.push({ id: "AG" + String(i + 1).padStart(3, "0"), name, team: TEAMS[i % TEAMS.length], skill, vol, baseTarget, refundBase, history });
  }
  return out;
}
const AGENTS = buildAgents(100);

const PERIODS = [
  { key: "today", label: "Today", factor: 1 / 22 }, { key: "yesterday", label: "Yesterday", factor: 1 / 22 },
  { key: "this_week", label: "This week", factor: 1 / 4.3 }, { key: "this_month", label: "This month", factor: 1 },
  { key: "last_30", label: "Last 30 days", factor: 1 }, { key: "last_month", label: "Last month", factor: 0.96 },
  { key: "rolling_q", label: "Rolling quarter", factor: 3 }, { key: "this_q", label: "This quarter", factor: 2.1 },
  { key: "last_q", label: "Last quarter", factor: 3 }, { key: "this_year", label: "This year", factor: 6.4 },
  { key: "all_time", label: "All time", factor: 27 },
];

function computeRow(agent, period) {
  const rng = mulberry32(hash(agent.id + "|" + period.key));
  const attain = Math.max(0.12, agent.skill + (gauss(rng) - 0.5) * agent.vol * 2.2);
  const target = Math.round(agent.baseTarget * period.factor);
  const gross = Math.round(target * attain);
  const refundRatio = Math.max(0.005, agent.refundBase + (gauss(rng) - 0.5) * 0.05);
  const refunds = Math.round(gross * refundRatio); const net = gross - refunds;
  return { ...agent, target, gross, net, refunds, refundRatio, progress: target > 0 ? net / target : 0 };
}
function statusOf(p) { if (p >= 1) return "on_target"; if (p >= 0.9) return "on_track"; if (p >= 0.7) return "at_risk"; return "behind"; }
const STATUS_META = {
  on_target: { label: "On target", c: C.green, bg: C.greenSoft },
  on_track: { label: "On track", c: C.indigo, bg: C.indigoSoft },
  at_risk: { label: "At risk", c: C.amber, bg: C.amberSoft },
  behind: { label: "Behind", c: C.red, bg: C.redSoft },
};
function trendOf(h) { const avg = h.reduce((a, b) => a + b, 0) / h.length; const std = Math.sqrt(h.reduce((a, b) => a + (b - avg) ** 2, 0) / h.length); const s = h[h.length - 1] - h[0]; if (std < 0.12 && avg >= 0.95) return "consistent"; if (s > 0.18) return "improving"; if (s < -0.18) return "declining"; if (std >= 0.22) return "volatile"; return "steady"; }
const TREND_META = { consistent: { label: "Consistent", c: C.green }, improving: { label: "Improving", c: C.indigo }, steady: { label: "Steady", c: C.ink50 }, volatile: { label: "Volatile", c: C.amber }, declining: { label: "Declining", c: C.red } };

function money(n) { const a = Math.abs(n); const s = n < 0 ? "-" : ""; if (a >= 1e6) return s + "$" + (a / 1e6).toFixed(a >= 1e7 ? 1 : 2) + "M"; if (a >= 1e3) return s + "$" + (a / 1e3).toFixed(a >= 1e4 ? 0 : 1) + "K"; return s + "$" + Math.round(a); }
const pct = (x) => Math.round(x * 100) + "%";
const initials = (n) => { const p = n.split(" "); return (p[0][0] + (p[1] ? p[1][0] : "")).toUpperCase(); };

// ---- product catalog (SaaS subscription tiers) ---------------------------
const PRODUCTS = [
  { key: "starter", name: "Starter", tier: "Entry", price: 29 },
  { key: "growth", name: "Growth", tier: "Team", price: 79 },
  { key: "pro", name: "Professional", tier: "Business", price: 149 },
  { key: "enterprise", name: "Enterprise", tier: "Enterprise", price: 349 },
  { key: "enterprise_plus", name: "Enterprise Plus", tier: "Enterprise", price: 699 },
];
const TIERS = ["Entry", "Team", "Business", "Enterprise"];
const PRODUCT_COLORS = { starter: C.indigo, growth: C.green, pro: C.amber, enterprise: C.red, enterprise_plus: "#8B5CF6" };
const POD_COLORS = { Apex: C.indigo, Titan: C.green, Falcon: C.amber, Orbit: C.red, Vanguard: "#8B5CF6" };
const PRIOR_PERIOD = { today: "yesterday", this_month: "last_month", this_q: "last_q" };

// each agent has a fixed, deterministic product mix (persona: SMB-leaning / balanced / Enterprise-leaning)
// so a given agent always sells the same relative blend of tiers, period to period.
function productWeights(agentId) {
  const rng = mulberry32(hash(agentId + "|productmix"));
  const persona = rng();
  const raw = PRODUCTS.map((p, i) => {
    const base = 0.4 + rng() * 1.2;
    let tierBoost = 1;
    if (persona < 0.34) tierBoost = i <= 1 ? 1.8 : (i === 2 ? 1 : 0.4); // SMB-leaning
    else if (persona < 0.68) tierBoost = 1; // balanced
    else tierBoost = i >= 3 ? 1.8 : (i === 2 ? 1 : 0.5); // enterprise-leaning
    return base * tierBoost;
  });
  const sum = raw.reduce((a, b) => a + b, 0);
  return raw.map(v => v / sum);
}

// allocates each agent's target/gross/net/refunds/history across products using their fixed mix,
// so summed product totals always tie back exactly to the agent/team totals for the same rows.
function computeProductRollup(rows) {
  const map = {};
  PRODUCTS.forEach(p => map[p.key] = { target: 0, gross: 0, net: 0, refunds: 0, histSum: [0, 0, 0, 0, 0, 0], weightSum: 0, teamBreakdown: {} });
  rows.forEach(r => {
    const w = productWeights(r.id);
    PRODUCTS.forEach((p, i) => {
      const m = map[p.key]; const wi = w[i];
      m.target += r.target * wi; m.gross += r.gross * wi; m.net += r.net * wi; m.refunds += r.refunds * wi; m.weightSum += wi;
      r.history.forEach((v, hi) => m.histSum[hi] += v * wi);
      m.teamBreakdown[r.team] = (m.teamBreakdown[r.team] || 0) + r.net * wi;
    });
  });
  return PRODUCTS.map(p => {
    const m = map[p.key];
    const history = m.histSum.map(v => v / (m.weightSum || 1));
    const progress = m.target ? m.net / m.target : 0;
    const refundRatio = m.gross ? m.refunds / m.gross : 0;
    return { ...p, target: m.target, gross: m.gross, net: m.net, refunds: m.refunds, progress, refundRatio, history, teamBreakdown: m.teamBreakdown, status: statusOf(progress), trend: trendOf(history) };
  });
}

// ---- calendar buckets for product trend chart (monthly / quarterly / yearly) ---
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthFactor(y, m) {
  // baseTarget already represents a "typical month" figure (see PERIODS: this_month factor=1),
  // so each calendar month gets a deterministic seasonal wiggle plus a gentle YoY growth drift.
  const idx = y * 12 + m;
  const rng = mulberry32(hash(`monthfactor|${y}-${m}`));
  const season = 0.85 + rng() * 0.3;
  const growth = 1 + ((idx - 2024 * 12) * 0.003);
  return Math.max(0.5, growth * season);
}
function buildMonthPool(count) {
  const today = new Date();
  const out = [];
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const y = d.getFullYear(), m = d.getMonth();
    out.push({ key: `${y}-${String(m + 1).padStart(2, "0")}`, y, m, quarter: Math.floor(m / 3) + 1, label: `${MONTH_NAMES[m]} '${String(y).slice(2)}` });
  }
  return out;
}
const MONTH_POOL = buildMonthPool(24); // 2 years of calendar months backing all three granularities

function computeMonthRow(agent, monthObj) {
  const rng = mulberry32(hash(agent.id + "|m|" + monthObj.key));
  const attain = Math.max(0.12, agent.skill + (gauss(rng) - 0.5) * agent.vol * 2.2);
  const target = Math.round(agent.baseTarget * monthFactor(monthObj.y, monthObj.m));
  const gross = Math.round(target * attain);
  const refundRatio = Math.max(0.005, agent.refundBase + (gauss(rng) - 0.5) * 0.05);
  const refunds = Math.round(gross * refundRatio);
  return { target, gross, net: gross - refunds, refunds };
}
// sums each agent's monthly figures across a set of calendar months into one bucket (quarter/year/etc)
function sumAgentRowsAcrossMonths(months) {
  return AGENTS.map(agent => {
    let target = 0, gross = 0, net = 0, refunds = 0;
    months.forEach(mo => { const r = computeMonthRow(agent, mo); target += r.target; gross += r.gross; net += r.net; refunds += r.refunds; });
    return { ...agent, target, gross, net, refunds };
  });
}
function buildBuckets(granularity, monthRange) {
  if (granularity === "month") {
    const n = monthRange === "6" ? 6 : monthRange === "12" ? 12 : MONTH_POOL.length;
    return MONTH_POOL.slice(MONTH_POOL.length - n).map(mo => ({ key: mo.key, label: mo.label, months: [mo] }));
  }
  const map = {};
  if (granularity === "quarter") {
    MONTH_POOL.forEach(mo => {
      const k = `${mo.y}-Q${mo.quarter}`;
      if (!map[k]) map[k] = { key: k, label: `Q${mo.quarter} '${String(mo.y).slice(2)}`, months: [], order: mo.y * 4 + mo.quarter };
      map[k].months.push(mo);
    });
  } else { // year
    MONTH_POOL.forEach(mo => {
      const k = `${mo.y}`;
      if (!map[k]) map[k] = { key: k, label: `${mo.y}`, months: [], order: mo.y };
      map[k].months.push(mo);
    });
  }
  return Object.values(map).sort((a, b) => a.order - b.order);
}

// ---- shared components ---------------------------------------------------
function Sparkline({ data, color, w = 60, h = 20 }) {
  const pad = 2; const min = Math.min(...data), max = Math.max(...data), span = max - min || 1;
  const pts = data.map((v, i) => [pad + (i / (data.length - 1)) * (w - pad * 2), h - pad - ((v - min) / span) * (h - pad * 2)]);
  const d = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  return (<svg width={w} height={h} style={{ display: "block" }}><path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2" fill={color} /></svg>);
}
function ProgressBar({ value, color }) {
  const capped = Math.min(value, 1.25);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ position: "relative", flex: 1, height: 7, background: C.lineSoft, borderRadius: 99, overflow: "hidden" }}>
        <div style={{ position: "absolute", left: `${(1 / 1.25) * 100}%`, top: 0, bottom: 0, width: 1, background: C.ink30 }} />
        <div style={{ height: "100%", width: `${(capped / 1.25) * 100}%`, background: color, borderRadius: 99, transition: "width .35s ease" }} />
      </div>
      <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: C.ink, minWidth: 40, textAlign: "right" }}>{pct(value)}</span>
    </div>
  );
}
function Pill({ label, color, bg }) {
  return (<span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 99, background: bg, color, fontSize: 11.5, fontWeight: 600 }}><span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />{label}</span>);
}
function SummaryCard({ label, value, sub, accent, bar }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
      <div style={{ fontFamily: FONT_DISP, fontSize: 26, fontWeight: 700, color: accent || C.ink, marginTop: 6, lineHeight: 1.1 }}>{value}</div>
      {bar != null && <div style={{ marginTop: 10 }}><ProgressBar value={bar} color={C.indigo} /></div>}
      {sub && <div style={{ fontSize: 12.5, color: C.ink50, marginTop: bar != null ? 8 : 6 }}>{sub}</div>}
    </div>
  );
}
function Th({ label, k, sortKey, sortDir, onSort, align = "left", w }) {
  const active = sortKey === k;
  return (
    <th onClick={k ? () => onSort(k) : undefined} style={{ textAlign: align, padding: "0 14px", height: 38, width: w, fontSize: 11.5, fontWeight: 600, color: active ? C.ink : C.ink50, textTransform: "uppercase", letterSpacing: 0.5, cursor: k ? "pointer" : "default", userSelect: "none", whiteSpace: "nowrap", position: "sticky", top: 0, background: C.linenDeep, zIndex: 2 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: align === "right" ? "flex-end" : "flex-start" }}>{label}{k && <span style={{ fontSize: 9, color: active ? C.indigo : C.ink30 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}</span>}</span>
    </th>
  );
}

// ==========================================================================
export default function NewDashboard() {
  const [view, setView] = useState("management"); // "agents" | "teams" | "management" | "products" | "agent-detail"
  const [periodKey, setPeriodKey] = useState("this_month");
  const [pickerOpen, setPickerOpen] = useState(false);
  const period = PERIODS.find(p => p.key === periodKey);

  // agent-detail state
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [cameFrom, setCameFrom] = useState("agents");
  function openAgent(id) { setCameFrom(view === "agent-detail" ? cameFrom : view); setSelectedAgentId(id); setView("agent-detail"); }
  function backToList() { setView(cameFrom); }
  const selectedAgent = AGENTS.find(a => a.id === selectedAgentId) || null;

  // agent-view state
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [statusSet, setStatusSet] = useState(new Set());
  const [refundOnly, setRefundOnly] = useState(false);
  const [aSortKey, setASortKey] = useState("progress");
  const [aSortDir, setASortDir] = useState("desc");

  // team-view state
  const [tSortKey, setTSortKey] = useState("progress");
  const [tSortDir, setTSortDir] = useState("desc");
  const [expanded, setExpanded] = useState(new Set());

  // ---- per-agent rows for the period ----
  const rows = useMemo(() => AGENTS.map(a => { const r = computeRow(a, period); r.status = statusOf(r.progress); r.trend = trendOf(a.history); return r; }), [periodKey]);

  // ---- teams rollup ----
  const teams = useMemo(() => {
    const map = {};
    TEAMS.forEach(t => map[t] = { name: t, agents: [], target: 0, gross: 0, net: 0, refunds: 0, onTarget: 0, histSum: [0, 0, 0, 0, 0, 0] });
    rows.forEach(r => {
      const t = map[r.team]; t.agents.push(r);
      t.target += r.target; t.gross += r.gross; t.net += r.net; t.refunds += r.refunds;
      if (r.status === "on_target") t.onTarget++;
      r.history.forEach((v, i) => t.histSum[i] += v);
    });
    return Object.values(map).map(t => {
      const n = t.agents.length;
      const history = t.histSum.map(v => v / n);
      const progress = t.target ? t.net / t.target : 0;
      const refundRatio = t.gross ? t.refunds / t.gross : 0;
      return { ...t, count: n, history, progress, refundRatio, status: statusOf(progress), trend: trendOf(history) };
    });
  }, [rows]);

  // ---- agent filtering/sort ----
  const agentsFiltered = useMemo(() => {
    let list = rows;
    if (search.trim()) { const q = search.toLowerCase(); list = list.filter(r => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q)); }
    if (teamFilter !== "all") list = list.filter(r => r.team === teamFilter);
    if (statusSet.size) list = list.filter(r => statusSet.has(r.status));
    if (refundOnly) list = list.filter(r => r.refundRatio >= 0.15);
    const dir = aSortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => aSortKey === "name" ? a.name.localeCompare(b.name) * dir : (a[aSortKey] - b[aSortKey]) * dir);
  }, [rows, search, teamFilter, statusSet, refundOnly, aSortKey, aSortDir]);

  const aSum = useMemo(() => { const s = { target: 0, gross: 0, net: 0, refunds: 0, onTarget: 0 }; agentsFiltered.forEach(r => { s.target += r.target; s.gross += r.gross; s.net += r.net; s.refunds += r.refunds; if (r.status === "on_target") s.onTarget++; }); s.progress = s.target ? s.net / s.target : 0; s.refundRatio = s.gross ? s.refunds / s.gross : 0; return s; }, [agentsFiltered]);

  // ---- team sort + summary ----
  const teamsSorted = useMemo(() => { const dir = tSortDir === "asc" ? 1 : -1; return [...teams].sort((a, b) => tSortKey === "name" ? a.name.localeCompare(b.name) * dir : (a[tSortKey] - b[tSortKey]) * dir); }, [teams, tSortKey, tSortDir]);
  const tSum = useMemo(() => { const s = { target: 0, gross: 0, net: 0, refunds: 0, agents: 0, onTargetTeams: 0 }; teams.forEach(t => { s.target += t.target; s.gross += t.gross; s.net += t.net; s.refunds += t.refunds; s.agents += t.count; if (t.status === "on_target") s.onTargetTeams++; }); s.progress = s.target ? s.net / s.target : 0; s.refundRatio = s.gross ? s.refunds / s.gross : 0; return s; }, [teams]);

  function aSort(k) { if (aSortKey === k) setASortDir(d => d === "asc" ? "desc" : "asc"); else { setASortKey(k); setASortDir(k === "name" ? "asc" : "desc"); } }
  function tSort(k) { if (tSortKey === k) setTSortDir(d => d === "asc" ? "desc" : "asc"); else { setTSortKey(k); setTSortDir(k === "name" ? "asc" : "desc"); } }
  function toggleStatus(k) { setStatusSet(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; }); }
  function toggleExpand(name) { setExpanded(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; }); }

  const selBar = { padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.line}`, background: C.card, fontFamily: FONT_BODY, fontSize: 13.5, color: C.ink, outline: "none", cursor: "pointer" };

  return (
    <div style={{ minHeight: "100vh", background: C.linen, fontFamily: FONT_BODY, color: C.ink, padding: "24px 28px 40px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box}table{border-collapse:collapse}
        ::-webkit-scrollbar{height:9px;width:9px}::-webkit-scrollbar-thumb{background:#D8C7AF;border-radius:9px}
        input::placeholder{color:${C.ink30}}
      `}</style>

      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 18 }}>
          <div>
            {view === "agent-detail" && (
              <button onClick={backToList} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 0 6px", border: "none", background: "none", cursor: "pointer", color: C.ink50, fontSize: 12.5, fontWeight: 600 }}>← Back to {cameFrom === "teams" ? "teams" : "agents"}</button>
            )}
            <div style={{ fontSize: 12, fontWeight: 600, color: C.indigo, textTransform: "uppercase", letterSpacing: 1.4 }}>Legendesk · Sales</div>
            <h1 style={{ fontFamily: FONT_DISP, fontSize: 30, fontWeight: 700, margin: "4px 0 0", letterSpacing: -0.5 }}>{view === "agents" ? "Agent Performance" : view === "teams" ? "Team Performance" : view === "management" ? "Management Overview" : view === "products" ? "Product Performance" : (selectedAgent ? selectedAgent.name : "Agent")}</h1>
          </div>
          <div style={{ position: "relative" }}>
            <button onClick={() => setPickerOpen(o => !o)} style={{ ...selBar, display: "inline-flex", alignItems: "center", gap: 10, fontWeight: 600 }}>
              <span style={{ width: 7, height: 7, borderRadius: 99, background: C.indigo }} />{period.label}<span style={{ color: C.ink30, fontSize: 11 }}>▼</span>
            </button>
            {pickerOpen && (
              <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: 6, boxShadow: "0 12px 30px rgba(10,12,18,.12)", zIndex: 20, width: 190 }}>
                {PERIODS.map(p => (<button key={p.key} onClick={() => { setPeriodKey(p.key); setPickerOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: p.key === periodKey ? 600 : 400, color: p.key === periodKey ? C.indigo : C.ink70, background: p.key === periodKey ? C.indigoSoft : "transparent" }}>{p.label}</button>))}
              </div>
            )}
          </div>
        </div>

        {/* view toggle */}
        {view !== "agent-detail" && (
          <div style={{ display: "inline-flex", background: C.linenDeep, borderRadius: 12, padding: 4, gap: 4, marginBottom: 18, flexWrap: "wrap" }}>
            {[["management", "Management"], ["products", "Products"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setView(k)} style={{ padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer", fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: 600, color: view === k ? C.ink : C.ink50, background: view === k ? C.card : "transparent", boxShadow: view === k ? "0 1px 3px rgba(10,12,18,.10)" : "none" }}>{lbl}</button>
            ))}
          </div>
        )}

        {view === "agents" ? (
          <AgentsView {...{ agentsFiltered, rows, aSum, period, search, setSearch, teamFilter, setTeamFilter, statusSet, toggleStatus, refundOnly, setRefundOnly, setStatusSet, aSortKey, aSortDir, aSort, selBar, onOpenAgent: openAgent }} />
        ) : view === "teams" ? (
          <TeamsView {...{ teamsSorted, tSum, period, tSortKey, tSortDir, tSort, expanded, toggleExpand, onOpenAgent: openAgent }} />
        ) : view === "management" ? (
          <ManagementView rows={rows} teams={teams} period={period} />
        ) : view === "products" ? (
          <ProductsView rows={rows} period={period} />
        ) : (
          <AgentDetailView agent={selectedAgent} rows={rows} period={period} onBack={backToList} />
        )}
      </div>
    </div>
  );
}

// ---- AGENTS VIEW (original) ----------------------------------------------
function AgentsView({ agentsFiltered, rows, aSum, period, search, setSearch, teamFilter, setTeamFilter, statusSet, toggleStatus, refundOnly, setRefundOnly, setStatusSet, aSortKey, aSortDir, aSort, selBar, onOpenAgent }) {
  return (<>
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
      <SummaryCard label="Active agents" value={agentsFiltered.length} sub={`${aSum.onTarget} on target · ${agentsFiltered.length - aSum.onTarget} below`} />
      <SummaryCard label="Total target" value={money(aSum.target)} sub={period.label} />
      <SummaryCard label="Net achieved" value={money(aSum.net)} bar={aSum.progress} />
      <SummaryCard label="Gross achieved" value={money(aSum.gross)} sub={`Net after refunds: ${money(aSum.net)}`} />
      <SummaryCard label="Lost revenue" value={money(aSum.refunds)} accent={C.red} sub={`Refund ratio ${pct(aSum.refundRatio)}`} />
    </div>

    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agent or ID…" style={{ ...selBar, cursor: "text", minWidth: 200, flex: "0 1 240px" }} />
      <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} style={selBar}><option value="all">All teams</option>{TEAMS.map(t => <option key={t} value={t}>Pod {t}</option>)}</select>
      <div style={{ display: "flex", gap: 6 }}>
        {Object.entries(STATUS_META).map(([k, m]) => {
          const on = statusSet.has(k); return (
            <button key={k} onClick={() => toggleStatus(k)} style={{ padding: "8px 11px", borderRadius: 99, border: `1px solid ${on ? m.c : C.line}`, background: on ? m.bg : C.card, color: on ? m.c : C.ink50, fontSize: 12.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: 99, background: m.c }} />{m.label}</button>
          );
        })}
      </div>
      <button onClick={() => setRefundOnly(v => !v)} style={{ padding: "8px 12px", borderRadius: 99, border: `1px solid ${refundOnly ? C.red : C.line}`, background: refundOnly ? C.redSoft : C.card, color: refundOnly ? C.red : C.ink50, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>High refunds only</button>
      {(statusSet.size > 0 || refundOnly || teamFilter !== "all" || search) && (
        <button onClick={() => { setStatusSet(new Set()); setRefundOnly(false); setTeamFilter("all"); setSearch(""); }} style={{ padding: "8px 12px", borderRadius: 99, border: "none", background: "transparent", color: C.ink50, fontSize: 12.5, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>Clear</button>
      )}
    </div>

    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
      <div style={{ maxHeight: 560, overflow: "auto" }}>
        <table style={{ width: "100%", minWidth: 940 }}>
          <thead><tr>
            <Th label="Agent" k="name" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} />
            <Th label="Target" k="target" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right" />
            <Th label="Gross" k="gross" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right" />
            <Th label="Net" k="net" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right" />
            <Th label="Lost" k="refunds" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right" />
            <Th label="Refund %" k="refundRatio" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} align="right" />
            <Th label="Progress" k="progress" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} w={190} />
            <Th label="Trend" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} />
            <Th label="Status" k="status" sortKey={aSortKey} sortDir={aSortDir} onSort={aSort} />
          </tr></thead>
          <tbody>
            {agentsFiltered.map((r, i) => {
              const sm = STATUS_META[r.status]; const tm = TREND_META[r.trend]; const bad = r.refundRatio >= 0.15; return (
                <tr key={r.id} onClick={() => onOpenAgent(r.id)} style={{ borderTop: `1px solid ${C.lineSoft}`, background: i % 2 ? "#FffdF9" : C.card, cursor: "pointer" }}>
                  <td style={{ padding: "10px 14px" }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, background: sm.bg, color: sm.c, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{initials(r.name)}</div>
                    <div style={{ minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: "nowrap" }}>{r.name}</div><div style={{ fontSize: 11.5, color: C.ink50 }}>{r.id} · Pod {r.team}</div></div>
                  </div></td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.ink70 }}>{money(r.target)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13 }}>{money(r.gross)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500 }}>{money(r.net)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.red }}>{money(r.refunds)}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: bad ? C.red : C.ink70, fontWeight: bad ? 600 : 400 }}>{pct(r.refundRatio)}</td>
                  <td style={{ padding: "10px 14px", width: 190 }}><ProgressBar value={r.progress} color={sm.c} /></td>
                  <td style={{ padding: "10px 14px" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Sparkline data={r.history} color={tm.c} /><span style={{ fontSize: 11.5, fontWeight: 600, color: tm.c }}>{tm.label}</span></div></td>
                  <td style={{ padding: "10px 14px" }}><Pill label={sm.label} color={sm.c} bg={sm.bg} /></td>
                </tr>
              );
            })}
            {agentsFiltered.length === 0 && <tr><td colSpan={9} style={{ padding: "48px 14px", textAlign: "center", color: C.ink50 }}>No agents match these filters. Clear a filter to see more.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.lineSoft}`, fontSize: 12, color: C.ink50, display: "flex", justifyContent: "space-between" }}>
        <span>Showing {agentsFiltered.length} of {rows.length} agents</span>
        <span>Progress measured on net vs target · {period.label}</span>
      </div>
    </div>
  </>);
}

// ---- TEAMS VIEW ----------------------------------------------------------
function TeamsView({ teamsSorted, tSum, period, tSortKey, tSortDir, tSort, expanded, toggleExpand, onOpenAgent }) {
  return (<>
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
      <SummaryCard label="Teams" value={teamsSorted.length} sub={`${tSum.agents} agents · ${tSum.onTargetTeams} pods on target`} />
      <SummaryCard label="Total target" value={money(tSum.target)} sub={period.label} />
      <SummaryCard label="Net achieved" value={money(tSum.net)} bar={tSum.progress} />
      <SummaryCard label="Gross achieved" value={money(tSum.gross)} sub={`Net after refunds: ${money(tSum.net)}`} />
      <SummaryCard label="Lost revenue" value={money(tSum.refunds)} accent={C.red} sub={`Refund ratio ${pct(tSum.refundRatio)}`} />
    </div>

    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
      <span style={{ fontSize: 12.5, color: C.ink50 }}>Tap a pod to see its agents. Sort by any column.</span>
    </div>

    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
      <div style={{ overflow: "auto" }}>
        <table style={{ width: "100%", minWidth: 960 }}>
          <thead><tr>
            <Th label="Team" k="name" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} />
            <Th label="Agents" k="count" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right" />
            <Th label="Target" k="target" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right" />
            <Th label="Gross" k="gross" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right" />
            <Th label="Net" k="net" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right" />
            <Th label="Lost" k="refunds" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right" />
            <Th label="Refund %" k="refundRatio" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} align="right" />
            <Th label="Progress" k="progress" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} w={190} />
            <Th label="Trend" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} />
            <Th label="Status" k="status" sortKey={tSortKey} sortDir={tSortDir} onSort={tSort} />
          </tr></thead>
          <tbody>
            {teamsSorted.map((t, i) => {
              const sm = STATUS_META[t.status]; const tm = TREND_META[t.trend]; const bad = t.refundRatio >= 0.15; const open = expanded.has(t.name);
              const topAgents = [...t.agents].sort((a, b) => b.progress - a.progress);
              return (<React.Fragment key={t.name}>
                <tr onClick={() => toggleExpand(t.name)} style={{ borderTop: `1px solid ${C.lineSoft}`, background: i % 2 ? "#FffdF9" : C.card, cursor: "pointer" }}>
                  <td style={{ padding: "12px 14px" }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ color: C.ink30, fontSize: 11, width: 12, display: "inline-block", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▶</span>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: sm.bg, color: sm.c, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{t.name[0]}</div>
                    <div><div style={{ fontFamily: FONT_DISP, fontWeight: 700, fontSize: 14 }}>Pod {t.name}</div><div style={{ fontSize: 11.5, color: C.ink50 }}>{t.onTarget} of {t.count} on target</div></div>
                  </div></td>
                  <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500 }}>{t.count}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.ink70 }}>{money(t.target)}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13 }}>{money(t.gross)}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, fontWeight: 600 }}>{money(t.net)}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.red }}>{money(t.refunds)}</td>
                  <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: bad ? C.red : C.ink70, fontWeight: bad ? 600 : 400 }}>{pct(t.refundRatio)}</td>
                  <td style={{ padding: "12px 14px", width: 190 }}><ProgressBar value={t.progress} color={sm.c} /></td>
                  <td style={{ padding: "12px 14px" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Sparkline data={t.history} color={tm.c} /><span style={{ fontSize: 11.5, fontWeight: 600, color: tm.c }}>{tm.label}</span></div></td>
                  <td style={{ padding: "12px 14px" }}><Pill label={sm.label} color={sm.c} bg={sm.bg} /></td>
                </tr>
                {open && (
                  <tr style={{ background: "#FBF4EA" }}>
                    <td colSpan={10} style={{ padding: "4px 14px 14px 46px" }}>
                      <div style={{ fontSize: 11.5, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, margin: "10px 0 8px" }}>Agents in Pod {t.name}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 8 }}>
                        {topAgents.map(a => {
                          const asm = STATUS_META[a.status]; return (
                            <div key={a.id} onClick={(e) => { e.stopPropagation(); onOpenAgent(a.id); }} style={{ display: "flex", alignItems: "center", gap: 10, background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: "8px 10px", cursor: "pointer" }}>
                              <span style={{ width: 7, height: 7, borderRadius: 99, background: asm.c, flexShrink: 0 }} />
                              <div style={{ minWidth: 0, flex: "0 0 120px" }}><div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div><div style={{ fontSize: 11, color: C.ink50 }}>{money(a.net)} net</div></div>
                              <div style={{ flex: 1, minWidth: 60 }}><ProgressBar value={a.progress} color={asm.c} /></div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>);
            })}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.lineSoft}`, fontSize: 12, color: C.ink50, display: "flex", justifyContent: "space-between" }}>
        <span>{teamsSorted.length} pods · {tSum.agents} agents</span>
        <span>Team target is the sum of its agents' targets · {period.label}</span>
      </div>
    </div>
  </>);
}

// ---- AGENT DETAIL — shared chart primitives -------------------------------
const thStyleDetail = { textAlign: "left", padding: "0 14px", height: 38, fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap", position: "sticky", top: 0, background: C.linenDeep };

function TrendChart({ categories, series, height = 240, yFormatter = money }) {
  const [gradId] = useState(() => "tcg" + Math.random().toString(36).slice(2, 9));
  const width = 680, padL = 58, padR = 18, padT = 18, padB = 30;
  const innerW = width - padL - padR, innerH = height - padT - padB;
  const allVals = series.flatMap(s => s.values);
  const maxV = Math.max(...allVals, 0.0001);
  const minV = Math.min(0, ...allVals);
  const span = (maxV - minV) || 1;
  const xFor = (i) => categories.length > 1 ? padL + (i / (categories.length - 1)) * innerW : padL + innerW / 2;
  const yFor = (v) => padT + innerH - ((v - minV) / span) * innerH;
  const ticks = 4;
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) => minV + (span * i / ticks));
  const showArea = series.length === 1;
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {series.map(s => (
          <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.ink70, fontWeight: 600, background: C.linenDeep, padding: "4px 10px", borderRadius: 99 }}>
            <span style={{ width: 8, height: 8, borderRadius: 99, background: s.color, display: "inline-block", flexShrink: 0 }} />{s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {showArea && (
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={series[0].color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={series[0].color} stopOpacity="0" />
            </linearGradient>
          </defs>
        )}
        {tickVals.map((t, i) => (
          <g key={i}>
            <line x1={padL} x2={width - padR} y1={yFor(t)} y2={yFor(t)} stroke={C.lineSoft} strokeWidth="1" strokeDasharray="2 4" />
            <text x={padL - 8} y={yFor(t) + 4} textAnchor="end" fontSize="10.5" fontFamily={FONT_MONO} fill={C.ink30}>{yFormatter(t)}</text>
          </g>
        ))}
        {categories.map((c, i) => (
          <text key={c + i} x={xFor(i)} y={height - 8} textAnchor="middle" fontSize="10.5" fontFamily={FONT_BODY} fill={C.ink50}>{c}</text>
        ))}
        {showArea && (() => {
          const s = series[0];
          const line = s.values.map((v, i) => (i ? "L" : "M") + xFor(i).toFixed(1) + " " + yFor(v).toFixed(1)).join(" ");
          const areaD = `${line} L ${xFor(s.values.length - 1).toFixed(1)} ${yFor(minV).toFixed(1)} L ${xFor(0).toFixed(1)} ${yFor(minV).toFixed(1)} Z`;
          return <path d={areaD} fill={`url(#${gradId})`} stroke="none" />;
        })()}
        {series.map(s => {
          const d = s.values.map((v, i) => (i ? "L" : "M") + xFor(i).toFixed(1) + " " + yFor(v).toFixed(1)).join(" ");
          return (
            <g key={s.label}>
              <path d={d} fill="none" stroke={s.color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              {s.values.map((v, i) => (
                <circle key={i} cx={xFor(i)} cy={yFor(v)} r="3.6" fill={C.card} stroke={s.color} strokeWidth="2.2">
                  <title>{`${categories[i]}: ${yFormatter(v)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ProgressRing({ value, color, size = 132, stroke = 12 }) {
  const capped = Math.min(Math.max(value, 0), 1);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - capped);
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.lineSoft} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset .4s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: FONT_DISP, fontWeight: 700, fontSize: 22, color: C.ink }}>{pct(value)}</div>
        <div style={{ fontSize: 10, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>of target</div>
      </div>
    </div>
  );
}

function CompositionBar({ gross, net, refunds }) {
  const netPct = gross ? Math.max(0, net / gross) : 0, refPct = gross ? Math.max(0, refunds / gross) : 0;
  return (
    <div>
      <div style={{ display: "flex", height: 14, borderRadius: 99, overflow: "hidden", background: C.lineSoft }}>
        <div style={{ width: `${netPct * 100}%`, background: C.green }} />
        <div style={{ width: `${refPct * 100}%`, background: C.red }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11.5, color: C.ink50 }}>
        <span style={{ color: C.green, fontWeight: 600 }}>Net {pct(netPct)}</span>
        <span style={{ color: C.red, fontWeight: 600 }}>Refunded {pct(refPct)}</span>
      </div>
    </div>
  );
}

// ---- AGENT DETAIL VIEW -----------------------------------------------------
function AgentDetailView({ agent, rows, period, onBack }) {
  const [metric, setMetric] = useState("net");
  const [compare, setCompare] = useState("team");
  const DEFAULT_KEYS = ["today", "this_week", "this_month", "this_q", "this_year", "all_time"];
  const [selKeys, setSelKeys] = useState(new Set(DEFAULT_KEYS));

  function togglePeriod(k) {
    setSelKeys(prev => { const n = new Set(prev); if (n.has(k)) { if (n.size > 1) n.delete(k); } else n.add(k); return n; });
  }
  const selectedPeriods = PERIODS.filter(p => selKeys.has(p.key));

  const report = useMemo(() => {
    if (!agent) return [];
    return selectedPeriods.map(p => {
      const r = computeRow(agent, p); r.status = statusOf(r.progress); return { ...r, periodLabel: p.label, periodKey: p.key };
    });
  }, [agent, selKeys]); // eslint-disable-line

  const compareStats = useMemo(() => {
    if (!agent || compare === "none") return null;
    return selectedPeriods.map(p => {
      const compRows = AGENTS.map(a => computeRow(a, p));
      const pool = compare === "team" ? compRows.filter(r => r.team === agent.team) : compRows;
      const n = pool.length || 1;
      const sum = (k) => pool.reduce((s, r) => s + r[k], 0);
      const targetSum = sum("target");
      return { target: targetSum / n, gross: sum("gross") / n, net: sum("net") / n, refunds: sum("refunds") / n, progress: targetSum ? sum("net") / targetSum : 0 };
    });
  }, [compare, selKeys, agent]); // eslint-disable-line

  if (!agent) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: C.ink50, background: C.card, border: `1px solid ${C.line}`, borderRadius: 16 }}>
        Agent not found.{" "}
        <button onClick={onBack} style={{ color: C.indigo, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Go back</button>
      </div>
    );
  }

  console.log("agent", agent)

  const currentRow = rows.find(r => r.id === agent.id) || { ...computeRow(agent, period), status: "behind" };
  const sm = STATUS_META[currentRow.status];
  const tm = TREND_META[currentRow.trend || trendOf(agent.history)];

  const teamRows = [...rows].filter(r => r.team === agent.team).sort((a, b) => b.progress - a.progress);
  const rank = teamRows.findIndex(r => r.id === agent.id) + 1;
  const teamCount = teamRows.length || 1;
  const teamAvgProgress = teamRows.reduce((s, r) => s + r.progress, 0) / teamCount;

  const metricLabel = { net: "Net", gross: "Gross", target: "Target", refunds: "Refunds", progress: "Progress" }[metric];
  const isPct = metric === "progress";
  const series = [{ label: agent.name.split(" ")[0], color: C.indigo, values: report.map(r => r[metric]) }];
  if (compareStats) series.push({ label: compare === "team" ? `Pod ${agent.team} avg` : "Company avg", color: C.ink30, values: compareStats.map(s => s[metric]) });

  const chip = (active) => ({ padding: "6px 12px", borderRadius: 99, border: `1px solid ${active ? C.indigo : C.line}`, background: active ? C.indigoSoft : C.card, color: active ? C.indigo : C.ink50, fontSize: 12.5, fontWeight: 600, cursor: "pointer" });
  const periodChip = (active) => ({ padding: "5px 10px", borderRadius: 99, border: `1px solid ${active ? C.ink : C.line}`, background: active ? C.ink : C.card, color: active ? C.card : C.ink50, fontSize: 11.5, fontWeight: 600, cursor: "pointer" });

  return (
    <div>
      {/* identity header */}
      <div style={{ display: "flex", gap: 16, alignItems: "center", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "18px 20px", marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: sm.bg, color: sm.c, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20, flexShrink: 0 }}>{initials(agent.name)}</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: FONT_DISP, fontWeight: 700, fontSize: 20 }}>{agent.name}</div>
          <div style={{ fontSize: 12.5, color: C.ink50, marginTop: 2 }}>{agent.id} · Pod {agent.team} · Rank #{rank} of {teamCount}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Pill label={sm.label} color={sm.c} bg={sm.bg} />
          <Pill label={tm.label} color={tm.c} bg={C.linenDeep} />
        </div>
      </div>

      {/* summary cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <SummaryCard label="Target" value={money(currentRow.target)} sub={period.label} />
        <SummaryCard label="Net achieved" value={money(currentRow.net)} bar={currentRow.progress} />
        <SummaryCard label="Gross achieved" value={money(currentRow.gross)} sub={`Net after refunds: ${money(currentRow.net)}`} />
        <SummaryCard label="Lost revenue" value={money(currentRow.refunds)} accent={C.red} sub={`Refund ratio ${pct(currentRow.refundRatio)}`} />
        <SummaryCard label="Pod average" value={pct(teamAvgProgress)} sub={`Agent is ${currentRow.progress >= teamAvgProgress ? "above" : "below"} Pod ${agent.team} average`} />
      </div>

      {/* filters */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "14px 16px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 78 }}>Metric</span>
          {[["net", "Net"], ["gross", "Gross"], ["target", "Target"], ["refunds", "Refunds"], ["progress", "Progress"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setMetric(k)} style={chip(metric === k)}>{lbl}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 78 }}>Compare</span>
          {[["none", "No comparison"], ["team", `Pod ${agent.team} avg`], ["company", "Company avg"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setCompare(k)} style={chip(compare === k)}>{lbl}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 78 }}>Periods</span>
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => togglePeriod(p.key)} style={periodChip(selKeys.has(p.key))}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* charts row */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, alignItems: "stretch" }}>
        <div style={{ flex: "2 1 420px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", minWidth: 320 }}>
          <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{metricLabel} across periods</div>
          <TrendChart categories={selectedPeriods.map(p => p.label)} series={series} yFormatter={isPct ? pct : money} />
        </div>
        <div style={{ flex: "1 1 240px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, minWidth: 220 }}>
          <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, alignSelf: "flex-start" }}>Progress this period</div>
          <ProgressRing value={currentRow.progress} color={sm.c} />
          <div style={{ width: "100%" }}>
            <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Revenue composition</div>
            <CompositionBar gross={currentRow.gross} net={currentRow.net} refunds={currentRow.refunds} />
          </div>
          <div style={{ width: "100%", paddingTop: 10, borderTop: `1px solid ${C.lineSoft}` }}>
            <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>6-period pattern</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Sparkline data={agent.history} color={tm.c} w={90} h={26} />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: tm.c }}>{tm.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* report table */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ overflow: "auto" }}>
          <table style={{ width: "100%", minWidth: 760 }}>
            <thead><tr>
              <th style={thStyleDetail}>Period</th>
              <th style={{ ...thStyleDetail, textAlign: "right" }}>Target</th>
              <th style={{ ...thStyleDetail, textAlign: "right" }}>Gross</th>
              <th style={{ ...thStyleDetail, textAlign: "right" }}>Net</th>
              <th style={{ ...thStyleDetail, textAlign: "right" }}>Lost</th>
              <th style={{ ...thStyleDetail, textAlign: "right" }}>Refund %</th>
              <th style={{ ...thStyleDetail, width: 170 }}>Progress</th>
              <th style={thStyleDetail}>Status</th>
            </tr></thead>
            <tbody>
              {report.map((r, i) => {
                const rsm = STATUS_META[r.status]; const bad = r.refundRatio >= 0.15; return (
                  <tr key={r.periodKey} style={{ borderTop: `1px solid ${C.lineSoft}`, background: i % 2 ? "#FffdF9" : C.card }}>
                    <td style={{ padding: "10px 14px", fontWeight: 600, fontSize: 13 }}>{r.periodLabel}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.ink70 }}>{money(r.target)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13 }}>{money(r.gross)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500 }}>{money(r.net)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.red }}>{money(r.refunds)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: bad ? C.red : C.ink70, fontWeight: bad ? 600 : 400 }}>{pct(r.refundRatio)}</td>
                    <td style={{ padding: "10px 14px", width: 170 }}><ProgressBar value={r.progress} color={rsm.c} /></td>
                    <td style={{ padding: "10px 14px" }}><Pill label={rsm.label} color={rsm.c} bg={rsm.bg} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.lineSoft}`, fontSize: 12, color: C.ink50 }}>
          Showing {report.length} of {PERIODS.length} periods · toggle periods above to add or remove rows
        </div>
      </div>
    </div>
  );
}

// ---- MANAGEMENT VIEW (exec overview) --------------------------------------
function HeroStat({ label, value, sub, deltaText, deltaPositive, progress, color }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 18, padding: "20px 24px", display: "flex", alignItems: "center", gap: 20, flex: "2 1 360px", minWidth: 280 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
        <div style={{ fontFamily: FONT_DISP, fontSize: 38, fontWeight: 700, color: C.ink, marginTop: 6, lineHeight: 1 }}>{value}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {deltaText && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 99, background: deltaPositive ? C.greenSoft : C.redSoft, color: deltaPositive ? C.green : C.red, fontSize: 12, fontWeight: 700 }}>{deltaPositive ? "▲" : "▼"} {deltaText}</span>}
          {sub && <span style={{ fontSize: 12.5, color: C.ink50 }}>{sub}</span>}
        </div>
      </div>
      <ProgressRing value={progress} color={color} size={92} stroke={9} />
    </div>
  );
}
function StatTile({ label, value, sub, accent }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: "12px 16px 12px 18px", flex: "1 1 150px", minWidth: 140, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: accent || C.indigo }} />
      <div style={{ fontSize: 10.5, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontFamily: FONT_DISP, fontSize: 19, fontWeight: 700, marginTop: 4, color: C.ink }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.ink50, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ManagementView({ rows, teams, period }) {
  const DEFAULT_KEYS = ["today", "this_week", "this_month", "this_q", "this_year", "all_time"];
  const [podFilter, setPodFilter] = useState(new Set());
  const [metric, setMetric] = useState("net");
  const [selKeys, setSelKeys] = useState(new Set(DEFAULT_KEYS));
  const [focus, setFocus] = useState("all");

  function togglePod(t) { setPodFilter(prev => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; }); }
  function togglePeriod(k) { setSelKeys(prev => { const n = new Set(prev); if (n.has(k)) { if (n.size > 1) n.delete(k); } else n.add(k); return n; }); }

  const activePods = podFilter.size ? TEAMS.filter(t => podFilter.has(t)) : TEAMS;
  const rowsScoped = useMemo(() => rows.filter(r => activePods.includes(r.team)), [rows, podFilter]); // eslint-disable-line
  const teamsScoped = useMemo(() => teams.filter(t => activePods.includes(t.name)), [teams, podFilter]); // eslint-disable-line

  const kpi = useMemo(() => {
    const s = { target: 0, gross: 0, net: 0, refunds: 0, onTarget: 0 };
    rowsScoped.forEach(r => { s.target += r.target; s.gross += r.gross; s.net += r.net; s.refunds += r.refunds; if (r.status === "on_target") s.onTarget++; });
    s.progress = s.target ? s.net / s.target : 0; s.refundRatio = s.gross ? s.refunds / s.gross : 0;
    return s;
  }, [rowsScoped]);

  const priorKey = PRIOR_PERIOD[period.key];
  const delta = useMemo(() => {
    if (!priorKey) return null;
    const priorPeriod = PERIODS.find(p => p.key === priorKey);
    const priorRows = AGENTS.filter(a => activePods.includes(a.team)).map(a => computeRow(a, priorPeriod));
    const priorNet = priorRows.reduce((s, r) => s + r.net, 0);
    if (!priorNet) return null;
    return (kpi.net - priorNet) / priorNet;
  }, [kpi.net, priorKey, podFilter]); // eslint-disable-line

  const selectedPeriods = PERIODS.filter(p => selKeys.has(p.key));
  const podSeries = useMemo(() => activePods.map(t => ({
    label: `Pod ${t}`, color: POD_COLORS[t],
    values: selectedPeriods.map(p => {
      const prows = AGENTS.filter(a => a.team === t).map(a => computeRow(a, p));
      const sum = (k) => prows.reduce((s, r) => s + r[k], 0);
      if (metric === "progress") return sum("target") ? sum("net") / sum("target") : 0;
      return sum(metric);
    })
  })), [activePods, selKeys, metric]); // eslint-disable-line

  const podRanked = [...teamsScoped].sort((a, b) => b.progress - a.progress);
  const sortedDesc = [...rowsScoped].sort((a, b) => b.progress - a.progress);
  const topPerformers = sortedDesc.slice(0, focus === "top" ? 14 : 8);
  const needsAttention = [...rowsScoped].filter(r => r.status === "behind" || r.status === "at_risk").sort((a, b) => a.progress - b.progress).slice(0, focus === "risk" ? 14 : 8);

  const chip = (active) => ({ padding: "5px 11px", borderRadius: 99, border: `1px solid ${active ? C.indigo : C.line}`, background: active ? C.indigoSoft : C.card, color: active ? C.indigo : C.ink50, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" });
  const periodChip = (active) => ({ padding: "5px 10px", borderRadius: 99, border: `1px solid ${active ? C.ink : C.line}`, background: active ? C.ink : C.card, color: active ? C.card : C.ink50, fontSize: 11.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 });
  const metricLabel = { net: "Net", gross: "Gross", target: "Target", refunds: "Refunds", progress: "Progress" }[metric];
  const isPct = metric === "progress";
  const priorLabel = priorKey ? PERIODS.find(p => p.key === priorKey).label : null;
  const rowLabelStyle = { fontSize: 11, fontWeight: 700, color: C.ink30, textTransform: "uppercase", letterSpacing: 0.6, minWidth: 66, flexShrink: 0 };
  const divider = { borderTop: `1px solid ${C.lineSoft}`, margin: "2px 0" };

  return (
    <div>
      {/* toolbar */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "12px 16px", marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={rowLabelStyle}>Pods</span>
          {TEAMS.map(t => (<button key={t} onClick={() => togglePod(t)} style={chip(podFilter.size === 0 || podFilter.has(t))}>{t}</button>))}
          {podFilter.size > 0 && <button onClick={() => setPodFilter(new Set())} style={{ padding: "4px 8px", background: "none", border: "none", color: C.ink50, fontSize: 11.5, fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>Reset</button>}
          <span style={{ width: 1, alignSelf: "stretch", background: C.lineSoft, margin: "0 2px" }} />
          <span style={rowLabelStyle}>Focus</span>
          {[["all", "All"], ["risk", "At risk"], ["top", "Top"]].map(([k, lbl]) => (<button key={k} onClick={() => setFocus(k)} style={chip(focus === k)}>{lbl}</button>))}
          <span style={{ width: 1, alignSelf: "stretch", background: C.lineSoft, margin: "0 2px" }} />
          <span style={rowLabelStyle}>Chart</span>
          {[["net", "Net"], ["gross", "Gross"], ["target", "Target"], ["refunds", "Refunds"], ["progress", "Progress"]].map(([k, lbl]) => (<button key={k} onClick={() => setMetric(k)} style={chip(metric === k)}>{lbl}</button>))}
        </div>
        <div style={divider} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* <span style={{width:1,alignSelf:"stretch",background:C.lineSoft,margin:"0 2px"}}/> */}
          <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, minWidth: 120, paddingBottom: 2 }}>
            {PERIODS.map(p => (<button key={p.key} onClick={() => togglePeriod(p.key)} style={periodChip(selKeys.has(p.key))}>{p.label}</button>))}
          </div>
        </div>
      </div>

      {/* hero + tiles */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "stretch" }}>
        <HeroStat label="Net revenue achieved" value={money(kpi.net)} progress={kpi.progress} color={C.indigo}
          deltaText={delta != null ? `${pct(Math.abs(delta))} vs ${priorLabel}` : null} deltaPositive={delta != null && delta >= 0}
          sub={`of ${money(kpi.target)} target · ${period.label}`} />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: "3 1 480px" }}>
          <StatTile label="Gross achieved" value={money(kpi.gross)} sub={`Refunded ${money(kpi.refunds)}`} accent={C.ink70} />
          <StatTile label="Refund ratio" value={pct(kpi.refundRatio)} sub="of gross revenue" accent={C.red} />
          <StatTile label="Pods on target" value={`${teamsScoped.filter(t => t.status === "on_target").length} / ${teamsScoped.length}`} sub="at 100%+" accent={C.green} />
          <StatTile label="Agents on target" value={`${kpi.onTarget} / ${rowsScoped.length}`} sub="at 100%+" accent={C.amber} />
        </div>
      </div>

      {/* charts */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, alignItems: "stretch" }}>
        <div style={{ flex: "2 1 420px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "18px 20px", minWidth: 320 }}>
          <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>{metricLabel} by pod across periods</div>
          <TrendChart categories={selectedPeriods.map(p => p.label)} series={podSeries} yFormatter={isPct ? pct : money} />
        </div>
        <div style={{ flex: "1 1 260px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "18px 20px", minWidth: 240 }}>
          <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 14 }}>Pod ranking · {period.label}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {podRanked.map((t, i) => {
              const sm = STATUS_META[t.status]; const lead = i === 0; return (
                <div key={t.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: lead ? "8px 10px" : "0", borderRadius: 12, background: lead ? C.amberSoft : "transparent" }}>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, fontWeight: 700, color: lead ? C.amber : C.ink30, width: 16, textAlign: "center" }}>{i + 1}</span>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: POD_COLORS[t.name], flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>Pod {t.name}</div>
                    <ProgressBar value={t.progress} color={sm.c} />
                  </div>
                </div>
              );
            })}
            {podRanked.length === 0 && <div style={{ fontSize: 12.5, color: C.ink50, textAlign: "center", padding: "20px 0" }}>No pods match this filter.</div>}
          </div>
        </div>
      </div>

      {/* agent lists */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {focus !== "risk" && (<MiniAgentTable title="Top performers" rows={topPerformers} accent={C.green} count={topPerformers.length} />)}
        {focus !== "top" && (<MiniAgentTable title="Needs attention" rows={needsAttention} accent={C.red} count={needsAttention.length} empty="No agents currently behind or at risk in this scope." />)}
      </div>
    </div>
  );
}

function MiniAgentTable({ title, rows, empty, accent }) {
  return (
    <div style={{ flex: "1 1 380px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden", minWidth: 320 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.lineSoft}`, fontSize: 12, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.6, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: accent || C.ink30, flexShrink: 0 }} />{title}
      </div>
      <div style={{ maxHeight: 380, overflow: "auto" }}>
        <table style={{ width: "100%" }}><tbody>
          {rows.map((r, i) => {
            const sm = STATUS_META[r.status]; return (
              <tr key={r.id} style={{ borderTop: i ? `1px solid ${C.lineSoft}` : "none" }}>
                <td style={{ padding: "9px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: sm.bg, color: sm.c, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{initials(r.name)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: C.ink50 }}>Pod {r.team} · {money(r.net)} net</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: "9px 16px", width: 130 }}><ProgressBar value={r.progress} color={sm.c} /></td>
                <td style={{ padding: "9px 16px", textAlign: "right", whiteSpace: "nowrap" }}><Pill label={sm.label} color={sm.c} bg={sm.bg} /></td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td style={{ padding: "28px 16px", textAlign: "center", color: C.ink50, fontSize: 12.5 }}>{empty || "No agents to show."}</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}

// ---- PRODUCTS VIEW ---------------------------------------------------------
function ProductsView({ rows, period }) {
  const [tierFilter, setTierFilter] = useState("all");
  const [metric, setMetric] = useState("net");
  const [granularity, setGranularity] = useState("month"); // "month" | "quarter" | "year"
  const [monthRange, setMonthRange] = useState("12"); // "6" | "12" | "all" (only used when granularity==="month")
  const [expanded, setExpanded] = useState(new Set());
  const [pSortKey, setPSortKey] = useState("progress");
  const [pSortDir, setPSortDir] = useState("desc");

  function toggleExpand(key) { setExpanded(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; }); }
  function pSort(k) { if (pSortKey === k) setPSortDir(d => d === "asc" ? "desc" : "asc"); else { setPSortKey(k); setPSortDir(k === "name" ? "asc" : "desc"); } }

  const products = useMemo(() => computeProductRollup(rows), [rows]);
  const productsFiltered = tierFilter === "all" ? products : products.filter(p => p.tier === tierFilter);
  const productsSorted = useMemo(() => {
    const dir = pSortDir === "asc" ? 1 : -1;
    return [...productsFiltered].sort((a, b) => pSortKey === "name" ? a.name.localeCompare(b.name) * dir : (a[pSortKey] - b[pSortKey]) * dir);
  }, [productsFiltered, pSortKey, pSortDir]);

  const pSum = useMemo(() => {
    const s = { target: 0, gross: 0, net: 0, refunds: 0, onTarget: 0 };
    productsFiltered.forEach(p => { s.target += p.target; s.gross += p.gross; s.net += p.net; s.refunds += p.refunds; if (p.status === "on_target") s.onTarget++; });
    s.progress = s.target ? s.net / s.target : 0; s.refundRatio = s.gross ? s.refunds / s.gross : 0;
    return s;
  }, [productsFiltered]);

  const buckets = useMemo(() => buildBuckets(granularity, monthRange), [granularity, monthRange]);
  const bucketRollups = useMemo(() => buckets.map(b => computeProductRollup(sumAgentRowsAcrossMonths(b.months))), [buckets]);

  const productSeries = useMemo(() => productsFiltered.map(p => ({
    label: p.name, color: PRODUCT_COLORS[p.key] || C.indigo,
    values: bucketRollups.map(roll => {
      const item = roll.find(x => x.key === p.key);
      if (!item) return 0;
      return metric === "progress" ? item.progress : item[metric];
    })
  })), [productsFiltered, bucketRollups, metric]);

  const chip = (active) => ({ padding: "5px 11px", borderRadius: 99, border: `1px solid ${active ? C.indigo : C.line}`, background: active ? C.indigoSoft : C.card, color: active ? C.indigo : C.ink50, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" });
  const metricLabel = { net: "Net", gross: "Gross", target: "Target", refunds: "Refunds", progress: "Progress" }[metric];
  const isPct = metric === "progress";
  const granularityLabel = { month: "month", quarter: "quarter", year: "year" }[granularity];

  const rowLabelStyle = { fontSize: 11, fontWeight: 700, color: C.ink30, textTransform: "uppercase", letterSpacing: 0.6, minWidth: 66, flexShrink: 0 };
  const divider = { borderTop: `1px solid ${C.lineSoft}`, margin: "2px 0" };

  const agent = {
    "id": "AG012",
    "name": "Sadia Mirza",
    "team": "Titan",
    "skill": 1.1655439017146514,
    "vol": 0.2648115878039971,
    "baseTarget": 21473,
    "refundBase": 0.05648460665950551,
    "history": [
      1.214355263355965,
      1.045824539835338,
      1.1794369966581306,
      1.3400663546026266,
      1.1019183450762366,
      1.2736347366637095
    ]
  }

  
  const currentRow = rows.find(r => r.id === agent.id) || { ...computeRow(agent, period), status: "behind" };
  const sm = STATUS_META[currentRow.status];
  const tm = TREND_META[currentRow.trend || trendOf(agent.history)];
  
  return (
    <div>
      {/* toolbar */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "12px 16px", marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={rowLabelStyle}>Tier</span>
          <button onClick={() => setTierFilter("all")} style={chip(tierFilter === "all")}>All</button>
          {TIERS.map(t => (<button key={t} onClick={() => setTierFilter(t)} style={chip(tierFilter === t)}>{t}</button>))}
          <span style={{ width: 1, alignSelf: "stretch", background: C.lineSoft, margin: "0 2px" }} />
          <span style={rowLabelStyle}>Chart</span>
          {[["net", "Net"], ["gross", "Gross"], ["target", "Target"], ["refunds", "Refunds"], ["progress", "Progress"]].map(([k, lbl]) => (<button key={k} onClick={() => setMetric(k)} style={chip(metric === k)}>{lbl}</button>))}
        </div>
        <div style={divider} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={rowLabelStyle}>View by</span>
          {[["month", "Monthly"], ["quarter", "Quarterly"], ["year", "Yearly"]].map(([k, lbl]) => (<button key={k} onClick={() => setGranularity(k)} style={chip(granularity === k)}>{lbl}</button>))}
          {granularity === "month" && (<>
            <span style={{ width: 1, alignSelf: "stretch", background: C.lineSoft, margin: "0 2px" }} />
            <span style={rowLabelStyle}>Range</span>
            {[["6", "Last 6 months"], ["12", "Last 12 months"], ["all", "All time"]].map(([k, lbl]) => (<button key={k} onClick={() => setMonthRange(k)} style={chip(monthRange === k)}>{lbl}</button>))}
          </>)}
        </div>
      </div>

      {/* hero + tiles */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "stretch" }}>
        <HeroStat label="Net revenue achieved" value={money(pSum.net)} progress={pSum.progress} color={C.indigo} sub={`of ${money(pSum.target)} target · ${period.label}`} />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: "3 1 480px" }}>
          <StatTile label="Gross achieved" value={money(pSum.gross)} sub={`Refunded ${money(pSum.refunds)}`} accent={C.ink70} />
          <StatTile label="Refund ratio" value={pct(pSum.refundRatio)} sub="of gross revenue" accent={C.red} />
          <StatTile label="Tiers on target" value={`${pSum.onTarget} / ${productsFiltered.length}`} sub="at 100%+" accent={C.green} />
        </div>
      </div>

      {/* chart */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, alignItems: "stretch" }}>
        <div style={{ flex: "2 1 420px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px" }}>
          <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{metricLabel} by product · {granularityLabel}ly</div>
          <TrendChart categories={buckets.map(b => b.label)} series={productSeries} yFormatter={isPct ? pct : money} />
        </div>

        <div style={{ flex: "1 1 240px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, minWidth: 220 }}>
          <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, alignSelf: "flex-start" }}>Progress this period</div>
          <ProgressRing value={currentRow.progress} color={sm.c} />
          <div style={{ width: "100%" }}>
            <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Revenue composition</div>
            <CompositionBar gross={currentRow.gross} net={currentRow.net} refunds={currentRow.refunds} />
          </div>
          <div style={{ width: "100%", paddingTop: 10, borderTop: `1px solid ${C.lineSoft}` }}>
            <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>6-period pattern</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Sparkline data={agent.history} color={tm.c} w={90} h={26} />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: tm.c }}>{tm.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* table */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ overflow: "auto" }}>
          <table style={{ width: "100%", minWidth: 980 }}>
            <thead><tr>
              <Th label="Product" k="name" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} />
              <Th label="Tier" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} />
              <Th label="Price/seat" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} align="right" />
              <Th label="Target" k="target" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} align="right" />
              <Th label="Gross" k="gross" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} align="right" />
              <Th label="Net" k="net" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} align="right" />
              <Th label="Lost" k="refunds" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} align="right" />
              <Th label="Refund %" k="refundRatio" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} align="right" />
              <Th label="Progress" k="progress" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} w={190} />
              <Th label="Trend" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} />
              <Th label="Status" k="status" sortKey={pSortKey} sortDir={pSortDir} onSort={pSort} />
            </tr></thead>
            <tbody>
              {productsSorted.map((p, i) => {
                const sm = STATUS_META[p.status]; const tm = TREND_META[p.trend]; const bad = p.refundRatio >= 0.15; const open = expanded.has(p.key);
                const breakdown = Object.entries(p.teamBreakdown).sort((a, b) => b[1] - a[1]);
                const maxVal = breakdown.length ? breakdown[0][1] : 1;
                return (<React.Fragment key={p.key}>
                  <tr onClick={() => toggleExpand(p.key)} style={{ borderTop: `1px solid ${C.lineSoft}`, background: i % 2 ? "#FffdF9" : C.card, cursor: "pointer" }}>
                    <td style={{ padding: "12px 14px" }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: C.ink30, fontSize: 11, width: 12, display: "inline-block", transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▶</span>
                      <div style={{ width: 8, height: 8, borderRadius: 99, background: PRODUCT_COLORS[p.key], flexShrink: 0 }} />
                      <div style={{ fontFamily: FONT_DISP, fontWeight: 700, fontSize: 13.5 }}>{p.name}</div>
                    </div></td>
                    <td style={{ padding: "12px 14px", fontSize: 12.5, color: C.ink50 }}>{p.tier}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.ink70 }}>${p.price}/mo</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.ink70 }}>{money(p.target)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13 }}>{money(p.gross)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, fontWeight: 600 }}>{money(p.net)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.red }}>{money(p.refunds)}</td>
                    <td style={{ padding: "12px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: bad ? C.red : C.ink70, fontWeight: bad ? 600 : 400 }}>{pct(p.refundRatio)}</td>
                    <td style={{ padding: "12px 14px", width: 190 }}><ProgressBar value={p.progress} color={sm.c} /></td>
                    <td style={{ padding: "12px 14px" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><Sparkline data={p.history} color={tm.c} /><span style={{ fontSize: 11.5, fontWeight: 600, color: tm.c }}>{tm.label}</span></div></td>
                    <td style={{ padding: "12px 14px" }}><Pill label={sm.label} color={sm.c} bg={sm.bg} /></td>
                  </tr>
                  {open && (
                    <tr style={{ background: "#FBF4EA" }}>
                      <td colSpan={11} style={{ padding: "4px 14px 14px 46px" }}>
                        <div style={{ fontSize: 11.5, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, margin: "10px 0 8px" }}>Where {p.name} revenue comes from</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 520 }}>
                          {breakdown.map(([teamName, val]) => (
                            <div key={teamName} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 99, background: POD_COLORS[teamName], flexShrink: 0 }} />
                              <span style={{ fontSize: 12.5, fontWeight: 600, width: 90, flexShrink: 0 }}>Pod {teamName}</span>
                              <div style={{ flex: 1, height: 8, borderRadius: 99, background: C.lineSoft, overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${(val / maxVal) * 100}%`, background: POD_COLORS[teamName], borderRadius: 99 }} />
                              </div>
                              <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: C.ink70, width: 70, textAlign: "right" }}>{money(val)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>);
              })}
              {productsSorted.length === 0 && <tr><td colSpan={11} style={{ padding: "48px 14px", textAlign: "center", color: C.ink50 }}>No products match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.lineSoft}`, fontSize: 12, color: C.ink50, display: "flex", justifyContent: "space-between" }}>
          <span>Showing {productsSorted.length} of {products.length} tiers</span>
          <span>Tap a tier to see revenue by pod · {period.label}</span>
        </div>
      </div>
    </div>
  );
}
